import type { ModelAssetNode } from '../assets/model-asset-types'

/**
 * 快照叶子不属于直接子装配根时的错误信息。
 */
const OUTSIDE_DIRECT_CHILD_ROOT_ERROR = 'assembly transform snapshot leaf is outside direct child root'

/**
 * 快照路径片段不满足稳定映射要求时的错误信息。
 */
const SNAPSHOT_PATH_INVALID_ERROR = 'assembly transform snapshot path invalid'

/**
 * 拆分模型资产节点的稳定层级 key。
 *
 * @param nodeKey 由加载链路生成的稳定节点 key。
 * @returns 去除空片段后的 nodeKey 层级片段。
 */
function splitNodeKeyPath(nodeKey: string): readonly string[] {
  return nodeKey.split('/').filter((segment) => segment.length > 0)
}

/**
 * 判断叶子 nodeKey 是否位于直接子装配根的 nodeKey 子树内。
 *
 * @param directChildRoot 直接子装配根节点。
 * @param leaf 需要生成快照路径的叶子节点。
 * @returns 位于子树内时返回相对路径片段。
 */
function resolveRelativeNodeKeySegments(
  directChildRoot: ModelAssetNode,
  leaf: ModelAssetNode,
): readonly string[] {
  const rootSegments = splitNodeKeyPath(directChildRoot.nodeKey)
  const leafSegments = splitNodeKeyPath(leaf.nodeKey)
  const isInsideRoot = rootSegments.every((segment, index) => leafSegments[index] === segment)

  if (!isInsideRoot || leafSegments.length < rootSegments.length) {
    throw new Error(OUTSIDE_DIRECT_CHILD_ROOT_ERROR)
  }

  return leafSegments.slice(rootSegments.length)
}

/**
 * 创建子装配内部叶子零件的 transform 快照路径。
 *
 * @param directChildRoot 直接子装配根；结果路径相对此节点，不包含它自身。
 * @param leaf 直接子装配子树内的叶子节点。
 * @returns 相对快照路径；叶子有 `instanceUuid` 时用它替换叶子 nodeKey 片段。
 * @throws 当叶子不是零件、不位于直接子装配根的子树内或相对路径为空时。
 */
export function createAssemblyTransformSnapshotPath(
  directChildRoot: ModelAssetNode,
  leaf: ModelAssetNode,
): readonly string[] {
  if (leaf.nodeType !== 'part') {
    throw new Error('assembly transform snapshot leaf must be a part')
  }

  const relativeSegments = [...resolveRelativeNodeKeySegments(directChildRoot, leaf)]

  if (relativeSegments.length === 0) {
    throw new Error('assembly transform snapshot path is empty')
  }

  // 第一版策略只替换叶子段，中间层仍保留 nodeKey 片段，避免跨层级同版本节点误匹配。
  if (relativeSegments.length > 0 && leaf.instanceUuid !== undefined && leaf.instanceUuid.length > 0) {
    relativeSegments[relativeSegments.length - 1] = leaf.instanceUuid
  }

  return relativeSegments
}

/**
 * 规范化 transform 快照路径，供映射表和诊断输出使用。
 *
 * @param path 相对直接子装配根的路径片段。
 * @returns 已校验片段以 `/` 拼接后的稳定字符串。
 * @throws 当路径为空，或任一片段为空、包含 `/` 或带有首尾空白时。
 */
export function normalizeAssemblyTransformSnapshotPath(path: readonly string[]): string {
  if (path.length === 0) {
    throw new Error(SNAPSHOT_PATH_INVALID_ERROR)
  }

  path.forEach((segment) => {
    const trimmedSegment = segment.trim()

    if (segment !== trimmedSegment || trimmedSegment.length === 0 || segment.includes('/')) {
      throw new Error(SNAPSHOT_PATH_INVALID_ERROR)
    }
  })

  return path.join('/')
}
